import { apiClient } from './apiClient';
import { useTenantStore } from '../stores/useTenantStore';
import type {
  ObservedShape,
  ShapeFloorPayload,
} from '@/components/blog-templates/shape/shapeFloor';

// =============================================================================
// Types
// =============================================================================

/**
 * Template type - now only 'auto' as builder template feature has been removed.
 * AI autonomously determines structure based on config.
 */
export type TemplateType = 'auto';

/**
 * Auto template configuration for AI-directed content generation.
 */
export interface AutoTemplateConfig {
  minSections?: number;      // null = AI decides
  maxSections?: number;      // null = AI decides
  includeFAQ?: boolean;      // hint to include FAQ section
  includeImages?: boolean;   // hint to include AI images
  includeList?: boolean;     // hint to include bullet list
  listType?: 'auto' | 'bullet' | 'numbered' | 'lettered';
  // Content limits
  maxFAQItems?: number;
  maxListItems?: number;
  // Image settings
  imageStyle?: 'photorealistic' | 'illustration' | '3d' | 'minimalist';
  minImages?: number;
  maxImages?: number;
  imagePosition?: 'auto' | 'after-intro' | 'per-section' | 'end-only';
  // Linking strategy
  linkStrategy?: 'word-density' | 'auto' | 'fixed' | 'density-based';
  internalLinkCount?: number;
  externalLinkCount?: number;
}

/**
 * Style preset for auto templates - applies consistent styling across all blocks.
 */
export interface StylePreset {
  defaultBlockStyle?: Record<string, string>;
  headingStyle?: Record<string, string>;
  listStyle?: Record<string, string>;
  blockquoteStyle?: Record<string, string>;
  calloutStyle?: Record<string, string>;
  imageStyle?: Record<string, string>;
  aiImageStyle?: Record<string, string>;
  faqStyle?: Record<string, string>;
  tableStyle?: Record<string, string>;
}

export interface TemplateDefaultConfig {
  targetWordCount?: number;
  tone?: 'professional' | 'casual' | 'authoritative' | 'conversational';
  includesFAQ?: boolean;
  includesSchema?: boolean;
  // Link configuration
  internalLinkCount?: number;  // null = AI decides
  externalLinkCount?: number;  // null = AI decides
  linkStrategy?: 'word-density' | 'auto' | 'fixed' | 'density-based';
  // Auto template specific
  autoConfig?: AutoTemplateConfig;
  // Style preset for auto templates
  stylePreset?: StylePreset;
  // AI formatting enhancement for complex blocks (tables, callouts, lists, FAQ)
  enhanceFormatting?: boolean;
}

/**
 * Lightweight template summary for listings (excludes HTML content).
 * Use this type for template galleries and lists.
 */
export interface BlogTemplateSummary {
  id: string;
  slug: string;
  name: string;
  description?: string;
  icon?: string;
  categories: string[];
  features: string[];
  default_config: TemplateDefaultConfig;
  is_popular: boolean;
  is_new: boolean;
  is_system: boolean;
  // Legacy fields removed: has_builder_blocks, template_type
  // All templates are now 'auto' type - AI autonomously determines structure
  // Clone tracking fields
  parent_template_id?: string; // ID of template this was cloned from
  is_customized: boolean; // True if created via customization flow
  parent_template_name?: string; // Name of parent template (for display)
  // Page builder compatibility
  page_builder?: string; // Page builder compatibility (e.g., 'elementor', 'avada', 'gutenberg')
  preview_image_url?: string; // Template screenshot URL
  usage_count?: number; // Usage count for social proof
  is_tagged?: boolean; // True if tagged for the requesting tenant
  // Phase 2 v1.2: per-recipe shape floor (typed mirror — parse via
  // parseShapeFloor before display use; malformed payloads degrade to
  // config-only rendering)
  shape_floor?: ShapeFloorPayload;
  // Phase 3: per-recipe gate_conflict (hold-from-advertising)
  gate_conflict?: boolean;
  // A2 observed-shape annotation — NOT served by the backend yet (verified
  // 2026-07-23). Forward-compatible passthrough only: render-gated n>=5 via
  // canShowObserved; never fabricated.
  observed_shape?: ObservedShape | null;
}



/**
 * Full template with HTML content.
 * Use this type when user selects a template for editing.
 */
export interface BlogTemplate extends BlogTemplateSummary {
  content_template: string;
  // Legacy builder_blocks field removed - all templates are now auto-generated
}

/**
 * Response from list endpoint (lightweight, no HTML content).
 */
export interface BlogTemplateSummaryListResponse {
  templates: BlogTemplateSummary[];
  total: number;
  recently_used?: string[];
}

/**
 * @deprecated Use BlogTemplateSummaryListResponse for list operations.
 * This type is kept for backward compatibility.
 */
export interface BlogTemplateListResponse {
  templates: BlogTemplate[];
  total: number;
}

export interface BlogTemplateCreate {
  slug: string;
  name: string;
  description?: string;
  icon?: string;
  categories?: string[];
  features?: string[];
  default_config?: TemplateDefaultConfig;
  content_template: string;
  // Legacy fields removed: builder_blocks, template_type
  // All templates are now auto-generated
  is_popular?: boolean;
  is_new?: boolean;
}

export interface BlogTemplateUpdate {
  name?: string;
  description?: string;
  icon?: string;
  categories?: string[];
  features?: string[];
  default_config?: TemplateDefaultConfig;
  content_template?: string;
  // Legacy field removed: builder_blocks
  is_popular?: boolean;
  is_new?: boolean;
  is_active?: boolean;
}

export interface BlogTemplateFilters {
  category?: string;
  include_inactive?: boolean;
  tagged_for?: string; // Filter to show only templates tagged for this client
  // Legacy field removed: template_type (all templates are now 'auto')
  page_builder?: string; // Filter by page builder compatibility (e.g., 'elementor', 'avada')
}

/**
 * Response for client tags endpoint.
 */
export interface ClientTagsResponse {
  template_id: string;
  client_tags: string[]; // List of tenant IDs
}

/**
 * Request for setting client tags.
 */
export interface SetClientTagsRequest {
  tenant_ids: string[];
}

/**
 * Request for cloning/customizing a template.
 */
export interface BlogTemplateCloneRequest {
  name: string;
  description?: string;
}

// =============================================================================
// Block Properties Types (Normalized Storage)
// =============================================================================

/**
 * Block properties stored in the normalized block_properties table.
 * Properties are separated from structural data for better query capabilities.
 */
export interface BlockProperties {
  id: string;
  template_id: string;
  block_id: string;
  block_type: string;
  properties: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

/**
 * Request for updating block properties (partial update, merged with existing).
 */
export interface BlockPropertiesUpdate {
  properties: Record<string, unknown>;
}

/**
 * Request for bulk upserting block properties for a template.
 */
export interface BlockPropertiesBulkUpsert {
  blocks: Array<{
    block_id: string;
    block_type: string;
    properties: Record<string, unknown>;
  }>;
}

// =============================================================================
// Service
// =============================================================================

const API_BASE = '/blog-templates';

/** Advisory template suggestion from the backend intent classifier. */
export interface SuggestedTemplateResponse {
  family: string;
  template_id: string | null;
  template_name: string | null;
  template_slug: string | null;
  confidence: number;
  suggested_word_count: number;
  reason: string;
  resolved_by: string;
}

export const blogTemplatesService = {
  /**
   * List all available blog templates (lightweight, no HTML content).
   * All templates are now visible to all users.
   * Use tagged_for filter to show only templates tagged for a specific client.
   * For performance, this excludes content_template - use get() to fetch full template.
   */
  async list(filters?: BlogTemplateFilters, signal?: AbortSignal): Promise<BlogTemplateSummaryListResponse> {
    const params = new URLSearchParams();
    // Pass tenant_id so backend can annotate is_tagged per template
    const tenant_id = useTenantStore.getState().currentTenant;
    if (!tenant_id) throw new Error('No tenant selected');
    params.append('tenant_id', tenant_id);
    if (filters?.category) params.append('category', filters.category);
    if (filters?.include_inactive) params.append('include_inactive', 'true');
    if (filters?.tagged_for) params.append('tagged_for', filters.tagged_for);
    // Legacy template_type filter removed - all templates are now 'auto'
    if (filters?.page_builder) params.append('page_builder', filters.page_builder);

    const response = await apiClient.get<BlogTemplateSummaryListResponse>(
      `${API_BASE}?${params.toString()}`,
      { signal, timeout: 10_000 } // 10s — fail fast if stuck behind zombie connections, React Query retries
    );
    return response.data;
  },

  /**
   * Get recommended templates for a specific page builder.
   * Returns popular templates that are compatible with the given page builder,
   * sorted by usage count for social proof.
   */
  async getRecommended(pageBuilder: string, limit: number = 3): Promise<BlogTemplateSummary[]> {
    const params = new URLSearchParams();
    params.append('page_builder', pageBuilder);
    params.append('limit', limit.toString());

    const response = await apiClient.get<BlogTemplateSummaryListResponse>(
      `${API_BASE}/recommended?${params.toString()}`
    );
    return response.data.templates;
  },

  /**
   * Suggest an intent-appropriate template for a keyword/title (advisory).
   * Backed by the deterministic backend classifier (GET /blog-creation/templates/suggest).
   * Returns the resolved template plus family/confidence; the user's pick stays authoritative.
   */
  async suggestTemplate(
    keyword: string,
    title?: string,
    signal?: AbortSignal
  ): Promise<SuggestedTemplateResponse> {
    const params = new URLSearchParams();
    const tenant_id = useTenantStore.getState().currentTenant;
    if (!tenant_id) throw new Error('No tenant selected');
    params.append('tenant_id', tenant_id);
    params.append('keyword', keyword);
    if (title) params.append('title', title);

    const response = await apiClient.get<SuggestedTemplateResponse>(
      `/blog-creation/templates/suggest?${params.toString()}`,
      { signal, timeout: 10_000 }
    );
    return response.data;
  },

  /**
   * Get a single template by ID.
   */
  async get(templateId: string): Promise<BlogTemplate> {
    const tenant_id = useTenantStore.getState().currentTenant;
    if (!tenant_id) throw new Error('No tenant selected');
    const response = await apiClient.get<BlogTemplate>(`${API_BASE}/${templateId}?tenant_id=${tenant_id}`);
    return response.data;
  },

  /**
   * Get a template by slug.
   */
  async getBySlug(slug: string): Promise<BlogTemplate> {
    const tenant_id = useTenantStore.getState().currentTenant;
    if (!tenant_id) throw new Error('No tenant selected');
    const response = await apiClient.get<BlogTemplate>(`${API_BASE}/slug/${slug}?tenant_id=${tenant_id}`);
    return response.data;
  },

  /**
   * Create a new global template.
   * Templates are global and can be associated with tenants via tags.
   */
  async create(data: BlogTemplateCreate): Promise<BlogTemplate> {
    const response = await apiClient.post<BlogTemplate>(`${API_BASE}`, data);
    return response.data;
  },

  /**
   * Update a template.
   * Note: System templates cannot be updated.
   */
  async update(templateId: string, updates: BlogTemplateUpdate): Promise<BlogTemplate> {
    const response = await apiClient.patch<BlogTemplate>(
      `${API_BASE}/${templateId}`,
      updates
    );
    return response.data;
  },

  /**
   * Delete a template.
   * Note: System templates cannot be deleted.
   */
  async delete(templateId: string): Promise<void> {
    await apiClient.delete(`${API_BASE}/${templateId}`);
  },

  /**
   * Clone/customize a template.
   *
   * Creates a new template based on the source with:
   * - All content and configuration copied
   * - parent_template_id set to track lineage
   * - is_customized set to true
   * - Owned by the current tenant
   *
   * Use this to create a customizable copy of system templates
   * or to duplicate your own templates.
   *
   * @param sourceTemplateId - ID of the template to clone
   * @param request - Name and optional description for the clone
   * @returns The newly created template
   */
  async clone(sourceTemplateId: string, request: BlogTemplateCloneRequest): Promise<BlogTemplate> {
    const tenant_id = useTenantStore.getState().currentTenant;
    if (!tenant_id) throw new Error('No tenant selected');
    const response = await apiClient.post<BlogTemplate>(
      `${API_BASE}/${sourceTemplateId}/clone?tenant_id=${tenant_id}`,
      request
    );
    return response.data;
  },

  /**
   * Check if a template can be cloned.
   * All templates can be cloned.
   */
  canClone(_template: BlogTemplateSummary | BlogTemplate): boolean {
    return true;
  },

  /**
   * Check if a template can be deleted.
   * Only non-system templates can be deleted.
   */
  canDelete(template: BlogTemplateSummary | BlogTemplate): boolean {
    return !template.is_system;
  },

  // =========================================================================
  // Client Tag Management
  // =========================================================================

  /**
   * Get all client tags for a template.
   * Returns the list of tenant IDs that have been tagged for this template.
   */
  async getTags(templateId: string): Promise<ClientTagsResponse> {
    const response = await apiClient.get<ClientTagsResponse>(
      `${API_BASE}/${templateId}/tags`
    );
    return response.data;
  },

  /**
   * Set all client tags for a template (replaces existing tags).
   */
  async setTags(templateId: string, tenantIds: string[]): Promise<ClientTagsResponse> {
    const response = await apiClient.put<ClientTagsResponse>(
      `${API_BASE}/${templateId}/tags`,
      { tenant_ids: tenantIds }
    );
    return response.data;
  },

  /**
   * Add a client tag to a template.
   */
  async addTag(templateId: string, tenantId: string): Promise<ClientTagsResponse> {
    const response = await apiClient.post<ClientTagsResponse>(
      `${API_BASE}/${templateId}/tags/${tenantId}`
    );
    return response.data;
  },

  /**
   * Remove a client tag from a template.
   */
  async removeTag(templateId: string, tenantId: string): Promise<ClientTagsResponse> {
    const response = await apiClient.delete<ClientTagsResponse>(
      `${API_BASE}/${templateId}/tags/${tenantId}`
    );
    return response.data;
  },

  /**
   * Toggle a client tag for a template.
   * If the client is currently tagged, removes the tag. Otherwise, adds it.
   */
  async toggleTag(templateId: string, tenantId: string, currentlyTagged: boolean): Promise<ClientTagsResponse> {
    if (currentlyTagged) {
      return this.removeTag(templateId, tenantId);
    } else {
      return this.addTag(templateId, tenantId);
    }
  },

  // =========================================================================
  // Block Properties API (Normalized Storage)
  // =========================================================================

  /**
   * Get all block properties for a template.
   * Returns properties for all blocks in the template.
   */
  async getBlockProperties(templateId: string): Promise<BlockProperties[]> {
    const response = await apiClient.get<BlockProperties[]>(
      `${API_BASE}/${templateId}/block-properties`
    );
    return response.data;
  },

  /**
   * Get properties for a specific block.
   */
  async getBlockProperty(templateId: string, blockId: string): Promise<BlockProperties> {
    const response = await apiClient.get<BlockProperties>(
      `${API_BASE}/${templateId}/blocks/${blockId}/properties`
    );
    return response.data;
  },

  /**
   * Update properties for a specific block.
   * Properties are merged with existing (not replaced).
   */
  async updateBlockProperty(
    templateId: string,
    blockId: string,
    update: BlockPropertiesUpdate
  ): Promise<BlockProperties> {
    const response = await apiClient.put<BlockProperties>(
      `${API_BASE}/${templateId}/blocks/${blockId}/properties`,
      update
    );
    return response.data;
  },

  /**
   * Bulk upsert block properties for a template.
   * This is the primary method for saving template changes.
   * Also cleans up properties for blocks that no longer exist.
   */
  async bulkUpsertBlockProperties(
    templateId: string,
    data: BlockPropertiesBulkUpsert
  ): Promise<BlockProperties[]> {
    const response = await apiClient.post<BlockProperties[]>(
      `${API_BASE}/${templateId}/block-properties/bulk`,
      data
    );
    return response.data;
  },

  /**
   * Delete properties for a specific block.
   */
  async deleteBlockProperty(templateId: string, blockId: string): Promise<void> {
    await apiClient.delete(`${API_BASE}/${templateId}/blocks/${blockId}/properties`);
  },
};

export default blogTemplatesService;
